import { InputAdornment } from "@mui/material";
import { useSuggestedValue } from "hooks/useSuggestedValue";
import { useMemo } from "react";
import {
  Control,
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldValues,
  Path,
} from "react-hook-form";

import { SuggestedIcon } from "./SuggestedIcon";

type SuggestedFieldRenderProps<T extends FieldValues> = {
  field: ControllerRenderProps<T, Path<T>>;
  fieldState: ControllerFieldState;
  isSuggested: boolean;
  /** InputAdornment with SparkleIcon, for use in TextField slotProps */
  sparkleAdornment: React.ReactElement;
  /** Standalone SparkleIcon, for use next to checkboxes or custom controls */
  sparkleIcon: React.ReactElement;
};

type SuggestedFieldProps<T extends FieldValues> = {
  name: Path<T>;
  control: Control<T>;
  needsReview: boolean;
  children: (props: SuggestedFieldRenderProps<T>) => React.ReactElement;
};

/**
 * A render-prop wrapper around Controller that computes suggestion state
 * and provides a sparkle adornment. Use for any field type that needs
 * AI suggestion indicators (checkboxes, custom numeric inputs, etc).
 *
 * For plain TextFields, use SuggestedTextField instead.
 *
 * @example
 * <SuggestedField name="containerNumber" control={control} needsReview={needsReview}>
 *   {({ field, isSuggested, sparkleAdornment }) => (
 *     <TextField
 *       value={field.value ?? ""}
 *       onChange={(e) => field.onChange(safeParseInt(e.target.value))}
 *       slotProps={{ input: { startAdornment: isSuggested ? sparkleAdornment : undefined } }}
 *     />
 *   )}
 * </SuggestedField>
 */
export function SuggestedField<T extends FieldValues>({
  name,
  control,
  needsReview,
  children,
}: SuggestedFieldProps<T>) {
  const isSuggestedValue = useSuggestedValue(needsReview);

  const sparkleIcon = useMemo(() => <SuggestedIcon isSuggested size="small" />, []);

  const sparkleAdornment = useMemo(
    () => <InputAdornment position="start">{sparkleIcon}</InputAdornment>,
    [sparkleIcon]
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) =>
        children({
          field,
          fieldState,
          isSuggested: isSuggestedValue(field.value, fieldState),
          sparkleAdornment,
          sparkleIcon,
        })
      }
    />
  );
}
